import { Component, Element, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';

import { hasSlotContent } from '../../../../services/utils';

/**
 * The `pk-countdown-timer` component is used to display a timer that counts down to a specific date.
 *
 * @slot days-separator - The separator for the days.
 * @slot hours-separator - The separator for the hours.
 * @slot minutes-separator - The separator for the minutes.
 * @slot seconds-separator - The separator for the seconds.
 *
 * @event pkDeadlineEnds - The event that is emitted when the deadline ends.
 *
 * @part timer - The component's container element.
 * @part days__value - The part of the component that contains the days value.
 * @part days__label - The part of the component that contains the days label.
 * @part days__separator - The part of the component that contains the days separator.
 * @part hours__value - The part of the component that contains the hours value.
 * @part hours__label - The part of the component that contains the hours label.
 * @part hours__separator - The part of the component that contains the hours separator.
 * @part minutes__value - The part of the component that contains the minutes value.
 * @part minutes__label - The part of the component that contains the minutes label.
 * @part minutes__separator - The part of the component that contains the minutes separator.
 * @part seconds__value - The part of the component that contains the seconds value.
 * @part seconds__label - The part of the component that contains the seconds label.
 * @part seconds__separator - The part of the component that contains the seconds separator.
 */
@Component({
  tag: 'pk-countdown-timer',
  styleUrl: './pk-countdown-timer.scss',
  shadow: true,
})
export class PkCountdownTimer {
  // Own Properties
  // ====================

  private daysSeparator?: HTMLElement;
  private hoursSeparator?: HTMLElement;
  private minutesSeparator?: HTMLElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkCountdownTimerElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() deadlineDate: Date;
  @State() remainingDays: string = '00';
  @State() remainingHours: string = '00';
  @State() remainingMinutes: string = '00';
  @State() remainingSeconds: string = '00';
  @State() timeIntervalId: NodeJS.Timeout;
  @State() formatObject: { HH: boolean; DD: boolean; mm: boolean; ss: boolean } = {
    HH: true,
    DD: true,
    mm: true,
    ss: true,
  };
  @State() hasDaysSeparator?: boolean;
  @State() hasHoursSeparator?: boolean;
  @State() hasMinutesSeparator?: boolean;

  // Public Property API
  // ========================

  /** The future date on when the timer will be set to `00:00:00:00`. */
  @Prop({ reflect: true }) deadline!: string | Date;

  /** It will specify what elements the countdown-timer component should shown.
   * E.g: `format="HH:mm"` means that only the `Hours` and `Minutes` items will be shown in the timer. */
  @Prop() format: string = 'DD:HH:mm:ss';

  /** Text wording for the `Days` label.
   * Passing `false` as value, e.g: days-label="false" will hide the label. */
  @Prop() daysLabel: string = 'Days';

  /** Text wording for the `Hours` label.
   * Passing `false` as value, e.g: hours-label="false" will hide the label. */
  @Prop() hoursLabel: string = 'Hours';

  /** Text wording for the `Minutes` label.
   * Passing `false` as value, e.g: minutes-label="false" will hide the label. */
  @Prop() minutesLabel: string = 'Minutes';

  /** Text wording for the `Seconds` label.
   * Passing `false` as value, e.g: seconds-label="false" will hide the label. */
  @Prop() secondsLabel: string = 'Seconds';

  // Prop lifecycle events
  // =======================

  @Watch('deadline')
  handleDeadlineChange() {
    // Make sure to clear interval time if the deadline property change
    // after the component has been loaded
    this.clearTimeInterval();

    if (!this.deadline) {
      this.resetTimerValues();
      console.error(`[pk-countdown-timer]: "deadline" property value is missing 😵!!`);
      return;
    }

    this.deadlineDate = this.deadline instanceof Date ? this.deadline : new Date(this.deadline);
    if (!this.isValidDate(this.deadlineDate)) {
      this.resetTimerValues();
      console.error(`[pk-countdown-timer]: "deadline" property have an invalid Date string/object value 🚫!!`);
      return;
    }

    this.setTimeInterval();
  }

  @Watch('format')
  handleFormatChange() {
    const arrayObj = this.format.split(':');
    this.formatObject = {
      DD: arrayObj.includes('DD'),
      HH: arrayObj.includes('HH'),
      mm: arrayObj.includes('mm'),
      ss: arrayObj.includes('ss'),
    };
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the deadline ends, timer get down to 00:00:00:00 */
  @Event() pkDeadlineEnds: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleFormatChange();
    this.handleDeadlineChange();
  }

  disconnectedCallback() {
    this.clearTimeInterval();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private isValidDate = (value: Date | string) => value instanceof Date && !isNaN(value.valueOf());

  private updateClock = () => {
    if (!this.deadlineDate) return;

    const t = this.getTimeRemaining(this.deadlineDate);

    if (t.distance <= 0) {
      this.resetTimerValues();
      this.clearTimeInterval();

      this.pkDeadlineEnds.emit();
      return;
    }

    // Handle leading zeros
    this.remainingDays = `${t.days}`.length < 2 ? `0${t.days}` : `${t.days}`;
    this.remainingHours = `0${t.hours}`.slice(-2);
    this.remainingMinutes = `0${t.minutes}`.slice(-2);
    this.remainingSeconds = `0${t.seconds}`.slice(-2);
  };

  private setTimeInterval = () => {
    this.timeIntervalId = setInterval(this.updateClock, 1000);
  };

  private clearTimeInterval = () => {
    clearInterval(this.timeIntervalId);
  };

  private getTimeRemaining = (time: Date) => {
    // Get today's date and time.
    const now = new Date().getTime();
    // Find the distance between now and the `deadline` date.
    const distance = time.getTime() - now;

    // Time calculations for days, hours, minutes and seconds.
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    return {
      distance,
      days,
      hours,
      minutes,
      seconds,
    };
  };

  private resetTimerValues = () => {
    this.remainingDays = '00';
    this.remainingHours = '00';
    this.remainingMinutes = '00';
    this.remainingSeconds = '00';
  };

  private handleDaysSeparator = () => {
    this.hasDaysSeparator = hasSlotContent(this.daysSeparator);
  };

  private handleHoursSeparator = () => {
    this.hasHoursSeparator = hasSlotContent(this.hoursSeparator);
  };

  private handleMinutesSeparator = () => {
    this.hasMinutesSeparator = hasSlotContent(this.minutesSeparator);
  };

  private get hasSeparator(): boolean {
    return this.hasDaysSeparator || this.hasHoursSeparator || this.hasMinutesSeparator;
  }

  // private getAllVisibleItems = () => this.timerRefElem.querySelectorAll('pk-countdown-item');

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div class="countdown-timer--container" part="timer">
        {this.formatObject.DD && (
          <pk-countdown-item
            class={{
              'mr-[--countdown-item--margin-right] last-of-type:mr-0': true,
              'separator-design': this.hasSeparator,
            }}
            exportparts="value:days__value, label:days__label, separator:days__separator"
          >
            <span>{this.remainingDays}</span>
            {this.daysLabel !== 'false' && <span slot="label">{this.daysLabel}</span>}
            <span
              slot="separator"
              class={{ hidden: !this.hasDaysSeparator }}
              ref={(daysSeparator: HTMLElement) => {
                this.daysSeparator = daysSeparator;
                this.handleDaysSeparator();
              }}
            >
              <slot name="days-separator" />
            </span>
          </pk-countdown-item>
        )}
        {this.formatObject.HH && (
          <pk-countdown-item
            class={{
              'mr-[--countdown-item--margin-right] last-of-type:mr-0': true,
              'separator-design': this.hasSeparator,
            }}
            exportparts="value:hours__value, label:hours__label, separator:hours__separator"
          >
            <span>{this.remainingHours}</span>
            {this.hoursLabel !== 'false' && <span slot="label">{this.hoursLabel}</span>}
            <span
              slot="separator"
              class={{ hidden: !this.hasHoursSeparator }}
              ref={(hoursSeparator: HTMLElement) => {
                this.hoursSeparator = hoursSeparator;
                this.handleHoursSeparator();
              }}
            >
              <slot name="hours-separator" />
            </span>
          </pk-countdown-item>
        )}
        {this.formatObject.mm && (
          <pk-countdown-item
            class={{
              'mr-[--countdown-item--margin-right] last-of-type:mr-0': true,
              'separator-design': this.hasSeparator,
            }}
            exportparts="value:minutes__value, label:minutes__label, separator:minutes__separator"
          >
            <span>{this.remainingMinutes}</span>
            {this.minutesLabel !== 'false' && <span slot="label">{this.minutesLabel}</span>}
            <span
              slot="separator"
              class={{ hidden: !this.hasMinutesSeparator }}
              ref={(minutesSeparator: HTMLElement) => {
                this.minutesSeparator = minutesSeparator;
                this.handleMinutesSeparator();
              }}
            >
              <slot name="minutes-separator" />
            </span>
          </pk-countdown-item>
        )}
        {this.formatObject.ss && (
          <pk-countdown-item
            class={{
              'mr-[--countdown-item--margin-right] last-of-type:mr-0': true,
              'separator-design': this.hasSeparator,
            }}
            exportparts="value:seconds__value, label:seconds__label, separator:seconds__separator"
          >
            <span>{this.remainingSeconds}</span>
            {this.secondsLabel !== 'false' && <span slot="label">{this.secondsLabel}</span>}
          </pk-countdown-item>
        )}
      </div>
    );
  }
}
